import React, { useState, useEffect } from 'react';
import { Users, Mail, Shield, Plus, X, Check, Edit2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: string;
  email: string;
  role: 'admin' | 'employee';
  created_at: string;
}

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    document.title = 'Benutzerverwaltung | TOK rec2rec Terminplaner';
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('list-users');
      
      if (error) throw error;
      if (data.users) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Create user in Supabase Auth using the admin API
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: newUserEmail,
          password: newUserPassword,
          role: 'employee'
        }
      });

      if (error) throw error;

      // Wait a moment for the trigger to create the user record
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Refresh user list
      await fetchUsers();
      
      toast.success('Benutzer erfolgreich erstellt');
      
      // Clear form
      setNewUserEmail('');
      setNewUserPassword('');
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error.message || 'Fehler beim Erstellen des Benutzers');
    }
  };

  const updateUserRole = async (userId: string, newRole: 'admin' | 'employee') => {
    try {
      const { error } = await supabase
        .from('users')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;

      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
      
      setEditingUser(null);
      toast.success('Benutzerrolle aktualisiert');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Fehler beim Aktualisieren der Benutzerrolle');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Benutzerverwaltung</h2>
        
        {/* Create New User Form */}
        <div className="bg-white p-6 rounded-lg shadow border-2 border-gray-200 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Neuen Benutzer erstellen</h3>
          <form onSubmit={createUser} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  E-Mail-Adresse
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    required
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="block w-full pl-10 rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                    placeholder="beispiel@domain.de"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Passwort
                </label>
                <div className="mt-1">
                  <input
                    type="password"
                    id="password"
                    required
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    className="block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                    placeholder="••••••••"
                  />
                </div>
              </div>
            </div>
            <div>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border-2 border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <Plus className="h-5 w-5 mr-2" />
                Benutzer erstellen
              </button>
            </div>
          </form>
        </div>

        {/* Users List */}
        <div className="bg-white rounded-lg shadow border-2 border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Benutzer</h3>
            <div className="space-y-4">
              {users.map(user => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center space-x-4">
                    <Users className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.email}</p>
                      <p className="text-sm text-gray-500">
                        Erstellt am: {new Date(user.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    {editingUser === user.id ? (
                      <div className="flex items-center space-x-2">
                        <select
                          value={user.role}
                          onChange={(e) => updateUserRole(user.id, e.target.value as 'admin' | 'employee')}
                          className="rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                        >
                          <option value="employee">Mitarbeiter</option>
                          <option value="admin">Administrator</option>
                        </select>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="p-2 text-gray-400 hover:text-gray-500"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            user.role === 'admin'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          <Shield className="h-4 w-4 mr-1" />
                          {user.role === 'admin' ? 'Administrator' : 'Mitarbeiter'}
                        </span>
                        {currentUser?.id !== user.id && (
                          <button
                            onClick={() => setEditingUser(user.id)}
                            className="p-2 text-gray-400 hover:text-gray-500"
                          >
                            <Edit2 className="h-5 w-5" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}

              {users.length === 0 && (
                <p className="text-center text-gray-500 py-4">
                  Keine Benutzer gefunden
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}